import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowRight, ChatCircleDots, Warning, Scales, Broadcast } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import CooConfidenceCard from "@/components/CooConfidenceCard";

function KPICard({ label, value, delta, unit }) {
  return (
    <div className="oz-card p-4">
      <div className="overline mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-mono text-2xl text-[color:var(--oz-text)]">{value}</div>
        {unit && <div className="text-xs text-[color:var(--oz-text-muted)] font-mono">{unit}</div>}
      </div>
      {delta !== undefined && (
        <div className={`text-xs mt-1 font-mono ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {delta >= 0 ? "+" : ""}{delta}
        </div>
      )}
    </div>
  );
}

function healthChip(h) {
  const cls = h === "red" ? "chip-red" : h === "yellow" ? "chip-yellow" : "chip-green";
  return <span className={`chip ${cls}`}><span className={`dot dot-${h}`} />{h?.toUpperCase()}</span>;
}

export default function Dashboard() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState("");
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [lastIntake, setLastIntake] = useState(null);
  const navigate = useNavigate();

  const loadBrief = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brief/daily");
      setBrief(data);
    } catch (e) {
      toast.error("Failed to load daily brief");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBrief(); }, []);

  const runSeed = async () => {
    toast.loading("Seeding demo operations data…", { id: "seed" });
    try {
      await api.post("/seed/demo");
      toast.success("Demo data loaded", { id: "seed" });
      await loadBrief();
    } catch (e) {
      toast.error("Seed failed", { id: "seed" });
    }
  };

  const submitIntake = async (e) => {
    e.preventDefault();
    if (!intake.trim()) return;
    setIntakeBusy(true);
    try {
      const { data } = await api.post("/intake", { text: intake });
      toast.success(`Classified → ${data.type.toUpperCase()}`);
      setLastIntake(data);
      setIntake("");
      await loadBrief();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Intake failed");
    } finally {
      setIntakeBusy(false);
    }
  };

  const c = brief?.counts || {};

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="overline mb-2">Daily Executive Brief</div>
          <h1 className="font-display text-4xl tracking-tight">Command</h1>
          <div className="text-xs text-[color:var(--oz-text-muted)] font-mono mt-1">
            {brief?.generated_at ? new Date(brief.generated_at).toLocaleString() : "…"}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="seed-btn" onClick={runSeed} className="btn-ghost text-sm">Load demo data</button>
          <button data-testid="refresh-brief" onClick={loadBrief} className="btn-primary text-sm">Refresh brief</button>
        </div>
      </div>

      {/* Universal Work Intake */}
      <div className="oz-cmdbar p-4 mb-8" data-testid="intake-panel">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={14} weight="fill" color="var(--oz-gold)" />
          <div className="overline">Universal Work Intake — Type anything. AI classifies it.</div>
        </div>
        <form onSubmit={submitIntake} className="flex gap-2">
          <input
            data-testid="intake-input"
            value={intake}
            onChange={(e) => setIntake(e.target.value)}
            placeholder='e.g. "Need to decide by Friday whether to accept Blackwater at $50k or hold for Aegis"'
            className="flex-1 bg-transparent border-none outline-none text-sm placeholder-[color:var(--oz-text-muted)]"
          />
          <button
            data-testid="intake-submit"
            type="submit"
            disabled={intakeBusy || !intake.trim()}
            className="btn-primary text-sm flex items-center gap-1"
          >
            {intakeBusy ? "Thinking…" : <>Capture <ArrowRight size={14} /></>}
          </button>
        </form>
        {lastIntake && (
          <div className="mt-3 pt-3 border-t border-[color:var(--oz-border)] text-xs text-[color:var(--oz-text-dim)] animate-fade-in">
            <span className="chip chip-gold mr-2">{lastIntake.type.toUpperCase()}</span>
            {lastIntake.rationale}
          </div>
        )}
      </div>

      {/* Confidence gauge + KPI row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard label="Critical Tasks" value={c.tasks_critical ?? "—"} unit="live" />
          <KPICard label="Red Health" value={c.tasks_red ?? "—"} unit="tasks" />
          <KPICard label="Decisions Pending" value={c.decisions_pending ?? "—"} unit="on you" />
          <KPICard label="Client Drafts Waiting" value={c.pending_drafts ?? "—"} unit="approvals" />
        </div>
        <CooConfidenceCard />
      </div>

      {/* Two-column layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Critical + Red Tasks */}
        <div className="oz-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg tracking-tight">Priority Board</h2>
            <Link to="/tasks" className="text-xs text-[color:var(--oz-text-dim)] hover:text-[color:var(--oz-emerald)] transition-colors" data-testid="link-all-tasks">
              All tasks →
            </Link>
          </div>
          {loading ? (
            <div className="text-sm text-[color:var(--oz-text-dim)]">Loading…</div>
          ) : (
            <div className="space-y-1">
              {[...(brief?.critical_tasks || []), ...(brief?.red_tasks || [])]
                .filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i)
                .slice(0, 8)
                .map((t) => (
                  <div key={t.id} className="oz-row py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[color:var(--oz-text)] truncate">{t.title}</div>
                      <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-0.5 font-mono">
                        {t.workstream} · {t.owner} · due {t.deadline ? new Date(t.deadline).toLocaleDateString() : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="chip chip-gold font-mono">{t.priority_score?.toFixed(1)}</span>
                      {healthChip(t.health)}
                    </div>
                  </div>
                ))}
              {(!brief?.critical_tasks?.length && !brief?.red_tasks?.length) && (
                <div className="text-sm text-[color:var(--oz-text-muted)] py-8 text-center">
                  Nothing critical. Load demo data to see this alive.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Decisions + Risks + Curtain quick */}
        <div className="space-y-6">
          <div className="oz-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Scales size={16} color="var(--oz-gold)" />
              <h2 className="font-display text-lg tracking-tight">Awaiting Decision</h2>
            </div>
            {(brief?.pending_decisions || []).slice(0, 3).map((d) => (
              <div key={d.id} className="oz-row py-3 last:border-b-0">
                <div className="text-sm text-[color:var(--oz-text)]">{d.title}</div>
                <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-1 font-mono truncate">
                  {d.recommendation}
                </div>
              </div>
            ))}
            {!(brief?.pending_decisions || []).length && (
              <div className="text-sm text-[color:var(--oz-text-muted)]">All clear.</div>
            )}
          </div>

          <div className="oz-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Warning size={16} color="var(--oz-red)" />
              <h2 className="font-display text-lg tracking-tight">Top Risks</h2>
            </div>
            {(brief?.top_risks || []).slice(0, 3).map((r) => (
              <div key={r.id} className="oz-row py-3 last:border-b-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-[color:var(--oz-text)] truncate">{r.title}</div>
                  <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-0.5 font-mono">
                    P{r.probability} × I{r.impact}
                  </div>
                </div>
                <span className={`chip ${r.level === "high" ? "chip-red" : r.level === "medium" ? "chip-yellow" : "chip-green"} font-mono`}>
                  {r.exposure}
                </span>
              </div>
            ))}
            {!(brief?.top_risks || []).length && <div className="text-sm text-[color:var(--oz-text-muted)]">No open risks.</div>}
          </div>

          <button
            data-testid="open-curtain-btn"
            onClick={() => navigate("/relay")}
            className="oz-card oz-glass w-full p-5 text-left group transition-colors hover:border-[color:var(--oz-gold)]"
          >
            <div className="flex items-center gap-2 mb-2">
              <ChatCircleDots size={16} color="var(--oz-gold)" />
              <div className="overline" style={{ color: "var(--oz-gold)" }}>The Curtain</div>
            </div>
            <div className="font-display text-lg tracking-tight mb-1">
              {c.pending_drafts || 0} draft{c.pending_drafts === 1 ? "" : "s"} awaiting your approval
            </div>
            <div className="text-xs text-[color:var(--oz-text-dim)]">
              Two-way relay chat · client thinks they're talking to you
            </div>
          </button>
        </div>
      </div>

      {/* KPIs */}
      {(brief?.kpis || []).length > 0 && (
        <div className="mt-8">
          <div className="overline mb-3">Key Performance Indicators</div>
          <div className="grid md:grid-cols-4 gap-4">
            {(brief.kpis).map((k) => {
              const pct = k.target ? Math.min(100, (k.actual / k.target) * 100) : 0;
              return (
                <div key={k.id} className="oz-card p-4">
                  <div className="text-xs text-[color:var(--oz-text-dim)] mb-1">{k.name}</div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <div className="font-mono text-xl">{k.actual?.toLocaleString?.() ?? k.actual}</div>
                    <div className="font-mono text-xs text-[color:var(--oz-text-muted)]">/ {k.target?.toLocaleString?.() ?? k.target} {k.unit}</div>
                  </div>
                  <div className="prio-bar"><div className="prio-fill" style={{ width: `${pct}%`, background: pct >= 90 ? "var(--oz-emerald)" : pct >= 60 ? "var(--oz-amber)" : "var(--oz-red)" }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
